import { ReactNode, useEffect } from 'react';

/**
 * 상세 화면의 **공통 껍데기** — 화면 한가운데 뜨는 팝업.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 오른쪽 서랍에서 가운데 팝업으로 옮겼나 (대표 QA, 2026-08-27)
 * ─────────────────────────────────────────────────────────────
 * 예전에는 상세가 화면 **오른쪽에 붙는 서랍**이었습니다. 두 가지가 걸렸습니다.
 *
 *   ① 목록은 가운데를 보다가 상세는 오른쪽 끝을 봐야 해서 시선이 매번 옮겨집니다.
 *      "집중이 끊긴다"는 말이 그것입니다.
 *   ② 서랍은 화면 높이를 꽉 채웁니다. 내용이 짧으면 **아래가 텅 빈 채**로 남습니다.
 *      회원 상세는 대부분 짧아서 늘 그랬습니다.
 *
 * 그래서 가운데 팝업으로 바꾸고, **세로는 내용에 맞춥니다.** 짧으면 짧게 뜨고
 * 길면 화면의 90% 까지만 커진 뒤 안에서 스크롤합니다. 빈 칸이 남지 않습니다.
 *
 * ⚠️ **가로 폭은 서랍 때와 같은 값을 그대로 씁니다** (브리핑 상세 880 등).
 *    안에 들어가는 카드가 그 폭에 맞춰 짜여 있어서, 폭까지 내용에 맞추면
 *    3단 구조가 무너지고 추천 문구가 여러 줄로 흐릅니다. 바뀐 것은 **자리와
 *    세로 크기뿐**이고, 내용은 한 글자도 건드리지 않았습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 껍데기를 하나로 뽑았나
 * ─────────────────────────────────────────────────────────────
 * 회원 상세 · 브리핑 상세 · 강사 데이터 세 화면이 **같은 관용구**를 세 벌
 * 베껴 쓰고 있었습니다. 실제로 그래서 한 번 갈렸습니다 — 카드가 찌그러져
 * 위가 잘리는 문제(2026-08-26)를 한쪽만 고치면 다른 쪽이 그대로 남았고,
 * drawerScrollLayout.test.tsx 가 "두 드로어는 같은 관용구를 쓴다. 한쪽만
 * 고치면 다음에 또 갈린다"고 적어 둔 이유가 그것입니다. 이제 한 곳입니다.
 *
 * ⚠️ 이 파일은 **틀만** 맡습니다. 안에 들어가는 내용은 각 화면이 넘깁니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 운영 브리핑의 층위표 (z-index) — 여기가 원본이다
 * ─────────────────────────────────────────────────────────────
 * 값이 파일마다 흩어져 있어 실제로 한 번 어긋났다. 2026-08-27 까지 선물 창이
 * `z-[90]`, 2차 비밀번호 창이 `z-[120]` 이었다 — 둘 다 **상세 안에서 열리는데**
 * 상세(2300)보다 낮아, 상세를 연 채 선물을 누르면 어두운 막 뒤로 깔렸다.
 * jsdom 에는 레이아웃이 없어 테스트가 못 잡는 종류다. 그래서 표를 한 곳에 적는다.
 *
 *   2000  MorningBriefingModal      아침에 한 번 뜨는 브리핑
 *   2200  CoachTaskMobilePreview    강사 앱 미리보기
 *   2300  OpsDetailModal            상세 — 회원 · 브리핑 · 강사 데이터  ← 이 파일
 *   2400  EvidenceModal · RawDataModal · CoachReportModal · ValueBreakdownModal
 *                                   상세 안에서 여는 근거 · 원천 데이터 · 리포트 고르기 ·
 *                                   금액 산출 근거 (이 파일을 `zIndex={2400}` 로 다시 씀)
 *   2500  OrderConfirmModal · OrderRetractModal    지시 확인 · 철회
 *   2600  BriefingGiftModal         선물 보내기 — 포인트가 나간다
 *   2700  useGiftSectionLock.gate   선물 2차 비밀번호 (선물 창 위에 덮는다)
 *   3000  CoachReportSheet · CoachPdfReport        A4 리포트 · 인쇄 미리보기
 *
 * **규칙: 어떤 창 안에서 여는 창은 반드시 그 창보다 위여야 한다.** 아래에 두면
 * 눌러도 아무 일이 안 일어난 것처럼 보인다 — 실제로 그렇게 보였다.
 * Tailwind `z-[…]` 와 인라인 `zIndex` 가 섞여 있지만 **같은 눈금**이다.
 * 값을 새로 만들거나 옮기면 이 표부터 고친다.
 */

/** 머리말 네이비 — 세 화면이 같은 머리말을 쓴다 */
const HEADER_BG = 'linear-gradient(135deg, #1A305D 0%, #2B5183 100%)';

/**
 * 열릴 때의 움직임.
 *
 * 서랍일 때는 오른쪽에서 밀려 나왔습니다. 가운데 팝업에 옆으로 미는 움직임을
 * 그대로 쓰면 어디서 나온 것인지 알 수 없어 오히려 산만합니다. 제자리에서
 * 아주 살짝 커지며 나타나는 정도로만 둡니다 (160ms).
 *
 * 움직임을 줄이도록 설정한 분에게는 아예 걸지 않습니다.
 */
const OPEN_ANIMATION = `
@keyframes opsDetailModalIn {
  from { opacity: 0; transform: scale(0.976) translateY(8px); }
  to   { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .ops-detail-modal { animation: none !important; }
}
`;

type Props = {
    /**
     * 이 창의 이름 (`role="dialog"` 의 접근성 이름).
     *
     * ⚠️ 화면·테스트가 `getByRole('dialog', { name })` 로 이 창을 집습니다.
     *    바꾸면 그 화면을 집던 검사가 전부 헛짚습니다.
     */
    label: string;
    /** 닫기 버튼의 이름. 이것도 테스트가 집는 이름이라 화면마다 다릅니다 */
    closeLabel: string;
    /**
     * 가로 폭(px). 서랍 때 쓰던 값을 그대로 넘깁니다 —
     * 안의 카드가 이 폭에 맞춰 짜여 있습니다.
     */
    width: number;
    /** 머리말 왼쪽 — 제목·배지·부제. 화면마다 다릅니다 */
    header: ReactNode;
    onClose: () => void;
    /**
     * Esc 를 이 창이 먹으면 안 되는 순간.
     *
     * 지시 확인 창이 이 위에 떠 있을 때 Esc 를 누르면 **확인 창만** 닫혀야 합니다.
     * 둘 다 닫히면 원장님은 취소를 눌렀을 뿐인데 보던 내용까지 잃습니다.
     */
    escapeLocked?: boolean;
    /**
     * 이 창이 놓일 층 — 기본은 상세의 2300 이다.
     *
     * ⚠️ **위 층위표를 고치지 않고 새 값을 넣지 마세요.** 상세 **안에서** 여는
     *    창은 반드시 2300 보다 위여야 하고(안 그러면 어두운 막 뒤로 깔려 눌러도
     *    아무 일이 안 난 것처럼 보인다), 지시 확인 창(2500)보다는 아래여야 한다.
     *    그 사이에 이미 2400 이 있으니 새 눈금을 만들지 말고 그것을 쓴다.
     */
    zIndex?: number;
    children: ReactNode;
};

const OpsDetailModal = ({
    label,
    closeLabel,
    width,
    header,
    onClose,
    escapeLocked = false,
    zIndex = 2300,
    children,
}: Props) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !escapeLocked) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, escapeLocked]);

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            /*
              어두운 막. z 는 2300 그대로 —
              근거·원천 데이터 팝업(2400) < 지시 확인 창(2500) < 리포트(3000) 아래다.
              올리면 상세 안에서 연 팝업이 상세 뒤로 깔려 눌러도 아무 일이 안 난 것처럼 보인다.
            */
            style={{ background: 'rgba(12,22,40,0.45)', zIndex, padding: 24 }}
            onClick={onClose}
        >
            <style>{OPEN_ANIMATION}</style>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={label}
                className="ops-detail-modal flex flex-col overflow-hidden rounded-[16px]"
                style={{
                    width,
                    maxWidth: '96vw',
                    /*
                      세로는 **내용에 맞춘다.** 높이를 정하지 않았으므로 짧은 상세는
                      짧게 뜬다 — 서랍처럼 아래가 비지 않는다.
                      길면 여기서 멈추고 본문이 안에서 스크롤한다.
                    */
                    maxHeight: '90vh',
                    background: '#F6F8FB',
                    boxShadow: '0 30px 80px rgba(10,20,40,0.34)',
                    animation: 'opsDetailModalIn 160ms ease-out',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 머리말 — 세 화면이 같은 네이비를 쓴다 */}
                <div
                    className="flex flex-none items-start justify-between"
                    style={{ background: HEADER_BG, padding: '22px 26px 20px' }}
                >
                    {header}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={closeLabel}
                        style={{
                            border: 'none',
                            background: 'rgba(255,255,255,0.14)',
                            color: '#FFFFFF',
                            width: 34,
                            height: 34,
                            borderRadius: 9,
                            cursor: 'pointer',
                            fontSize: 17,
                            flex: 'none',
                            marginLeft: 16,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/*
                  본문.

                  ⚠️ **`flex flex-col` 로 두지 마십시오.** 여기는 세로 flex 안이라,
                     본문까지 flex 컨테이너로 만들면 안의 카드가 `flex-shrink:1` 인
                     자식이 되어 **스크롤을 만들지 않고 찌그러집니다.** 카드 루트에
                     `overflow:hidden` 이 걸려 있어 min-height 가 0 으로 풀리기 때문입니다.
                     그러면 압축량을 카드 안에서 overflow:hidden 이 걸린 블록(근거 수치
                     스트립)이 혼자 흡수하고, 그 칸들이 `justify-center` 라 위아래로 넘쳐
                     **위가 잘린 채** 보입니다. 실제로 60일 수치 블록(0회/4회/105일 전)이
                     머리말 바로 밑에서 잘려 보였습니다 (drawerScrollLayout.test.tsx 가 지킵니다).

                     일반 블록으로 두면 자식이 자연 높이로 쌓이고 이 칸이 스크롤합니다.

                  ⚠️ `flexBasis: 'auto'` 를 지우지 마십시오. Tailwind `flex-1` 은
                     `flex: 1 1 0%` 이고, 높이를 정하지 않은(= 내용에 맞추는) 창 안에서
                     기준 크기 0 은 **본문이 0 으로 접힐 여지**를 남깁니다. 기준을 내용
                     크기로 되돌려야 "짧으면 짧게, 길면 90vh 에서 스크롤"이 둘 다 됩니다.
                */}
                <div
                    className="flex-1 overflow-y-auto"
                    style={{ padding: 16, flexBasis: 'auto', minHeight: 0 }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

export default OpsDetailModal;
